import { CustomSpin } from '@components/Common/CustomSpin';
import { ProvideState, useAuthProvider } from '@modules/auth/providers/AuthProvider/AuthProvider';
import {
  checkIfShouldForceGenericAuth,
  useCommonAuthProvider,
} from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { getCookie } from '@utils/CookieUtil';

import { captureException } from '@sentry/nextjs';
import { asyncWithLDProvider, useLDClient } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { testScenarioCookieName } from 'shared/src/__tests__/common/testFixtures';

const FeatureFlagsLoader: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [areFlagsLoading, setAreaFlagsLoading] = React.useState<boolean>(true);
  const ldClient = useLDClient();
  const { user } = useCommonAuthProvider();
  const { authorizedOrganizationId: organizationId, setProvider, provider } = useAuthProvider();

  React.useEffect(() => {
    if (ldClient === undefined) {
      return;
    }

    const waitForFlags = async (): Promise<void> => {
      try {
        const testScenario = getCookie(testScenarioCookieName);

        const generateLDKey = (): string => {
          if (
            (user?.email as string)?.startsWith('testautomation+') ||
            (user?.email as string)?.startsWith('testintegration')
          ) {
            return 'test-user';
          }

          return user?.sub?.split('|')?.pop() || 'anonymous-loggedin-user';
        };

        const customLDFields: Record<string, string | number | boolean> = {};

        if (organizationId) {
          customLDFields.currentTenant = organizationId;
        }

        if (testScenario) {
          customLDFields.testScenario = testScenario;
        }

        if (process.env.AUTH_CLIENT_PROVIDER) {
          customLDFields.authProvider = process.env.AUTH_CLIENT_PROVIDER;
        }

        await ldClient.waitUntilReady();
        await ldClient.identify({
          key: generateLDKey(),
          name: `${user?.given_name} ${user?.family_name}`,
          email: user?.email,
          custom: customLDFields,
        });
      } catch (error) {
        captureException(error);
      } finally {
        const shouldForceGenericAuth = checkIfShouldForceGenericAuth();
        const isGenericProvider = ldClient.variation('Cloud-Generic-Auth-Client', false);
        setProvider(isGenericProvider || shouldForceGenericAuth ? ProvideState.OIDC : ProvideState.AUTH0);

        setAreaFlagsLoading(false);
      }
    };

    waitForFlags();
  }, [ldClient, user, organizationId]);

  if (ldClient === undefined || areFlagsLoading || provider === 'LOADING') {
    return <CustomSpin />;
  }

  return <>{children}</>;
};

export const FeatureFlagsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [FeatureFlagsComponent, setFeatureFlagsComponent] = React.useState<null | Awaited<
    ReturnType<typeof asyncWithLDProvider>
  >>(null);

  React.useLayoutEffect(() => {
    const loadLDProvider = async (): Promise<void> => {
      try {
        const LDProviderComponent = await asyncWithLDProvider({
          clientSideID: process.env.LAUNCH_DARKLY_LD_CLIENT_SIDE_ID as string,
          options: {
            baseUrl: process.env.LAUNCH_DARKLY_LD_BASE_URL,
            eventsUrl: process.env.LAUNCH_DARKLY_LD_EVENTS_URL,
            streamUrl: process.env.LAUNCH_DARKLY_LD_STREAM_URL,
          },
        });

        setFeatureFlagsComponent(() => LDProviderComponent);

        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('LDProviderLoaded'));
        }
      } catch (error) {
        captureException(error);
        window.reload();
      }
    };

    loadLDProvider();
  }, []);

  if (FeatureFlagsComponent === null) {
    return <CustomSpin />;
  }

  return (
    <FeatureFlagsComponent>
      <FeatureFlagsLoader>{children}</FeatureFlagsLoader>
    </FeatureFlagsComponent>
  );
};
